import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  FaSpinner,
  FaUsers,
  FaWeight,
  FaSmile,
  FaCamera,
  FaClipboardCheck,
  FaExclamationTriangle,
  FaSearch,
  FaFilter,
  FaChevronRight,
} from 'react-icons/fa';
import { useAuth } from '../../context/AuthContext';
import { coachAPI } from '../../services/coachAPI';
import { toast } from 'react-toastify';

const moodEmojis = ['', '😞', '😕', '😐', '🙂', '😄'];

const CoachDashboard = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [clients, setClients] = useState([]);
  const [filteredClients, setFilteredClients] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterPlan, setFilterPlan] = useState('');
  const [sortBy, setSortBy] = useState('lastCheckIn');

  useEffect(() => {
    loadClients();
  }, []);

  useEffect(() => {
    filterAndSortClients();
  }, [clients, searchTerm, filterPlan, sortBy]);

  const loadClients = async () => {
    try {
      const data = await coachAPI.getClientsWithProgress();
      setClients(data);
    } catch (error) {
      console.error('Failed to load clients:', error);
      toast.error('Failed to load client data');
    } finally {
      setLoading(false);
    }
  };

  const filterAndSortClients = () => {
    let result = [...clients];

    // Filter by search term
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      result = result.filter(
        (c) =>
          c.firstName?.toLowerCase().includes(term) ||
          c.lastName?.toLowerCase().includes(term) ||
          c.email?.toLowerCase().includes(term)
      );
    }

    // Filter by plan type
    if (filterPlan) {
      result = result.filter((c) => c.planType === filterPlan);
    }

    // Sort
    result.sort((a, b) => {
      switch (sortBy) {
        case 'lastCheckIn':
          // Clients without check-ins come first (need attention)
          if (!a.lastCheckInDate && !b.lastCheckInDate) return 0;
          if (!a.lastCheckInDate) return -1;
          if (!b.lastCheckInDate) return 1;
          return new Date(b.lastCheckInDate) - new Date(a.lastCheckInDate);
        case 'name':
          return `${a.firstName} ${a.lastName}`.localeCompare(
            `${b.firstName} ${b.lastName}`
          );
        case 'weightChange':
          return (b.weightChange || 0) - (a.weightChange || 0);
        case 'overdue':
          return (b.daysSinceLastCheckIn || 999) - (a.daysSinceLastCheckIn || 999);
        default:
          return 0;
      }
    });

    setFilteredClients(result);
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'Never';
    return new Date(dateString).toLocaleDateString('en-IN', {
      day: 'numeric',
      month: 'short',
    });
  };

  const formatWeight = (weight) => {
    if (!weight) return '-';
    return `${weight.toFixed(1)} kg`;
  };

  const formatWeightChange = (change) => {
    if (!change) return '-';
    const prefix = change > 0 ? '+' : '';
    return `${prefix}${change.toFixed(1)} kg`;
  };

  const getOverdueStatus = (days) => {
    if (days === null || days === undefined) return { text: 'No check-in', color: 'text-gray-400' };
    if (days <= 3) return { text: `${days}d ago`, color: 'text-green-600' };
    if (days <= 7) return { text: `${days}d ago`, color: 'text-amber-600' };
    return { text: `${days}d ago`, color: 'text-red-600' };
  };

  // Stats
  const totalClients = clients.length;
  const activeClients = clients.filter((c) => c.subscriptionStatus === 'ACTIVE').length;
  const overdueClients = clients.filter((c) => c.daysSinceLastCheckIn > 7).length;
  const avgWeightLoss = clients.reduce((sum, c) => sum + (c.weightChange || 0), 0) / (clients.length || 1);

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-50 to-white flex items-center justify-center">
        <FaSpinner className="animate-spin text-4xl text-primary-600" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-white">
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="bg-gradient-to-r from-primary-600 to-primary-700 rounded-2xl p-8 text-white mb-8 shadow-lg">
          <h1 className="text-3xl font-bold mb-2">
            Welcome, Coach {user?.firstName}!
          </h1>
          <p className="text-primary-100 text-lg">
            Manage your clients and track their progress
          </p>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
          <div className="bg-white rounded-xl shadow-lg p-5 flex items-center gap-4">
            <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center">
              <FaUsers className="text-xl text-blue-600" />
            </div>
            <div>
              <p className="text-sm text-gray-500">Total Clients</p>
              <p className="text-2xl font-bold text-gray-900">{totalClients}</p>
            </div>
          </div>
          <div className="bg-white rounded-xl shadow-lg p-5 flex items-center gap-4">
            <div className="w-12 h-12 bg-green-100 rounded-xl flex items-center justify-center">
              <FaClipboardCheck className="text-xl text-green-600" />
            </div>
            <div>
              <p className="text-sm text-gray-500">Active</p>
              <p className="text-2xl font-bold text-green-600">{activeClients}</p>
            </div>
          </div>
          <div className="bg-white rounded-xl shadow-lg p-5 flex items-center gap-4">
            <div className="w-12 h-12 bg-red-100 rounded-xl flex items-center justify-center">
              <FaExclamationTriangle className="text-xl text-red-600" />
            </div>
            <div>
              <p className="text-sm text-gray-500">Overdue Check-ins</p>
              <p className="text-2xl font-bold text-red-600">{overdueClients}</p>
            </div>
          </div>
          <div className="bg-white rounded-xl shadow-lg p-5 flex items-center gap-4">
            <div className="w-12 h-12 bg-purple-100 rounded-xl flex items-center justify-center">
              <FaWeight className="text-xl text-purple-600" />
            </div>
            <div>
              <p className="text-sm text-gray-500">Avg Weight Change</p>
              <p className="text-2xl font-bold text-purple-600">
                {formatWeightChange(avgWeightLoss)}
              </p>
            </div>
          </div>
        </div>

        {/* Filters */}
        <div className="bg-white rounded-2xl shadow-lg p-6 mb-6">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div className="relative">
              <FaSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
              <input
                type="text"
                placeholder="Search clients..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              />
            </div>
            <div>
              <select
                value={filterPlan}
                onChange={(e) => setFilterPlan(e.target.value)}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              >
                <option value="">All Plans</option>
                <option value="CORE">Core</option>
                <option value="MEDICAL">Medical</option>
                <option value="GLP1">GLP-1</option>
              </select>
            </div>
            <div>
              <select
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value)}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              >
                <option value="lastCheckIn">Last Check-in</option>
                <option value="overdue">Most Overdue</option>
                <option value="name">Name</option>
                <option value="weightChange">Weight Change</option>
              </select>
            </div>
            <div className="flex items-center text-sm text-gray-500">
              <FaFilter className="mr-2" />
              Showing {filteredClients.length} of {clients.length} clients
            </div>
          </div>
        </div>

        {/* Client Cards */}
        {filteredClients.length === 0 ? (
          <div className="bg-white rounded-2xl shadow-lg p-12 text-center">
            <FaUsers className="text-5xl text-gray-300 mx-auto mb-4" />
            <h3 className="text-xl font-semibold text-gray-700 mb-2">
              {clients.length === 0 ? 'No Clients Assigned' : 'No Matching Clients'}
            </h3>
            <p className="text-gray-500">
              {clients.length === 0
                ? 'You have no clients assigned yet. Contact admin to get clients.'
                : 'Try adjusting your search or filters.'}
            </p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredClients.map((client) => {
              const overdueStatus = getOverdueStatus(client.daysSinceLastCheckIn);
              return (
                <Link
                  key={client.id}
                  to={`/coach-dashboard/client/${client.id}`}
                  className="bg-white rounded-2xl shadow-lg p-6 hover:shadow-xl transition-all hover:scale-[1.02] cursor-pointer group"
                >
                  <div className="flex items-start justify-between mb-4">
                    <div className="flex items-center gap-3">
                      <div className="w-12 h-12 bg-gradient-to-br from-primary-500 to-primary-600 rounded-full flex items-center justify-center text-white font-bold text-lg">
                        {client.firstName?.charAt(0)}
                        {client.lastName?.charAt(0)}
                      </div>
                      <div>
                        <h3 className="font-semibold text-gray-900 group-hover:text-primary-600 transition-colors">
                          {client.firstName} {client.lastName}
                        </h3>
                        <p className="text-sm text-gray-500">{client.email}</p>
                      </div>
                    </div>
                    <FaChevronRight className="text-gray-400 group-hover:text-primary-600 transition-colors" />
                  </div>

                  {/* Plan Badge */}
                  <div className="flex flex-wrap gap-2 mb-4">
                    {client.planType && (
                      <span
                        className={`px-3 py-1 rounded-full text-xs font-medium ${
                          client.planType === 'GLP1'
                            ? 'bg-purple-100 text-purple-800'
                            : client.planType === 'MEDICAL'
                            ? 'bg-blue-100 text-blue-800'
                            : 'bg-green-100 text-green-800'
                        }`}
                      >
                        {client.planType}
                      </span>
                    )}
                    <span className="px-3 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-700">
                      Week {client.currentWeek || 1}
                    </span>
                    {client.daysSinceLastCheckIn > 7 && (
                      <span className="px-3 py-1 rounded-full text-xs font-medium bg-red-100 text-red-800">
                        Needs Attention
                      </span>
                    )}
                  </div>

                  {/* Stats Grid */}
                  <div className="grid grid-cols-3 gap-3 text-center mb-4">
                    <div className="bg-gray-50 rounded-lg p-2">
                      <div className="flex items-center justify-center gap-1 text-gray-500 text-xs mb-1">
                        <FaWeight />
                        <span>Weight</span>
                      </div>
                      <p className="font-semibold text-gray-900 text-sm">
                        {formatWeight(client.latestWeight)}
                      </p>
                      {client.weightChange !== null && (
                        <p
                          className={`text-xs ${
                            client.weightChange < 0
                              ? 'text-green-600'
                              : client.weightChange > 0
                              ? 'text-red-600'
                              : 'text-gray-500'
                          }`}
                        >
                          {formatWeightChange(client.weightChange)}
                        </p>
                      )}
                    </div>
                    <div className="bg-gray-50 rounded-lg p-2">
                      <div className="flex items-center justify-center gap-1 text-gray-500 text-xs mb-1">
                        <FaSmile />
                        <span>Mood</span>
                      </div>
                      <p className="font-semibold text-2xl">
                        {moodEmojis[client.latestMood] || '-'}
                      </p>
                    </div>
                    <div className="bg-gray-50 rounded-lg p-2">
                      <div className="flex items-center justify-center gap-1 text-gray-500 text-xs mb-1">
                        <FaCamera />
                        <span>Photos</span>
                      </div>
                      <p className="font-semibold text-gray-900 text-sm">
                        {client.totalPhotos || 0}
                      </p>
                    </div>
                  </div>

                  {/* Footer */}
                  <div className="flex items-center justify-between text-sm border-t pt-3">
                    <span className="text-gray-500">
                      {client.totalCheckIns || 0} check-ins
                    </span>
                    <span className={overdueStatus.color}>
                      Last: {client.lastCheckInDate ? formatDate(client.lastCheckInDate) : 'Never'}
                    </span>
                  </div>
                </Link>
              );
            })}
          </div>
        )}
      </main>
    </div>
  );
};

export default CoachDashboard;
